import { useEffect, useState } from "react";
import Image from "next/image";
import classNames from "classnames";
import { useClickOutside } from "@mantine/hooks";
import { isBoolean } from "lodash";

import Chevron from "static/icons/chevron.svg";
import { FilledButton, OutlinedButton } from "components/common/Button/Button";
import useTranslation from "next-translate/useTranslation";
import classes from "./PriceFilter.module.css";
import { TextInput } from "../TextInput/TextInput";
import useInput from "../../../../hooks/useInput";
import { AnnouncementFiltersKey } from "../../../Layout/NavBar/Filters/Filters";

type FilterProps = {
  selectedValue: string[];
  placeholder: string;
  filterName: AnnouncementFiltersKey;
  onChangeHandler: (value: string[]) => void;
  onSubmitHandler?: () => void;
  onClearHandler?: (filterName: AnnouncementFiltersKey) => void;
  expandedInPlace?: boolean;
  closeOnClickOutside?: boolean;
  onClickToggle?: (filterName: string, openValue: boolean) => void;
  isFilterOpen?: boolean;
};

const PriceFilter = ({
  selectedValue,
  onChangeHandler,
  placeholder,
  filterName,
  onClearHandler,
  onSubmitHandler,
  expandedInPlace,
  closeOnClickOutside = true,
  onClickToggle,
  isFilterOpen,
}: FilterProps) => {
  const {
    value: priceFromValue,
    isTouched: priceFromIsTouched,
    error: priceFromError,
    // setIsTouched: setEmailIsTouched,
    // setError: setEmailError,
    eventValueChangeHandler: priceFromChangeHandler,
    inputBlurHandler: priceFromInputBlurHandler,
    reset: priceFromReset,
  } = useInput(selectedValue?.[0]);

  const {
    value: priceToValue,
    isTouched: priceToIsTouched,
    error: priceToError,
    // setIsTouched: setEmailIsTouched,
    // setError: setEmailError,
    eventValueChangeHandler: priceToChangeHandler,
    inputBlurHandler: priceToInputBlurHandler,
    reset: priceToReset,
  } = useInput(selectedValue?.[1]);

  useEffect(() => {
    if (priceToValue?.length) {
      onChangeHandler([priceFromValue || "0", priceToValue]);
    } else {
      onChangeHandler([priceFromValue || "0"]);
    }
  }, [priceFromValue, priceToValue]);

  const [isOpen, setIsOpen] = useState(false);
  const ref = useClickOutside(() => setIsOpen(false));
  const { t } = useTranslation("common");

  useEffect(() => {
    if (isBoolean(isFilterOpen)) {
      setIsOpen(isFilterOpen);
    }
  }, [isFilterOpen]);

  const clearValuesHandler = () => {
    setIsOpen(false);
    onClearHandler && onClearHandler(filterName);
  };

  const submitFilterHandler = () => {
    setIsOpen(false);
    onSubmitHandler && onSubmitHandler();
  };

  const clickToggleHandler = (isOpenValue: boolean) => {
    onClickToggle
      ? onClickToggle(filterName, isOpenValue)
      : setIsOpen(isOpenValue);
  };

  let optionsContent;

  return (
    <div
      ref={closeOnClickOutside ? ref : undefined}
      className={classNames(classes.container, {
        [classes.expandedInPlace]: expandedInPlace,
        [classes.active]: isOpen,
      })}
    >
      <div
        {...(!isOpen && {
          onClick: () => clickToggleHandler(true),
        })}
        className={classNames(classes.select, {
          [classes.expandedInPlace]: expandedInPlace,
        })}
      >
        {placeholder}
        <div
          onClick={() => clickToggleHandler(!isOpen)}
          className={classNames(classes.iconContainer, {
            [classes.iconContainerActive]: isOpen,
          })}
        >
          <Image src={Chevron} alt="Chevron" />
        </div>
      </div>
      <div
        className={classNames(
          classes.dropdown,
          { [classes.active]: isOpen },
          {
            [classes.expandedInPlace]: expandedInPlace,
          }
        )}
      >
        <div
          className={classNames(classes.inputsContainer, {
            [classes.expandedInPlace]: expandedInPlace,
          })}
        >
          <div className={classNames(classes.inputContainer)}>
            <TextInput
              id="priceFrom"
              name="priceFrom"
              label={t("price_from")}
              value={priceFromValue}
              placeholder={t("from")}
              onChangeHandler={priceFromChangeHandler}
              onBlurHandler={priceFromInputBlurHandler}
              onClearHandler={priceFromReset}
              error={priceFromIsTouched && !!priceFromError}
              helperText={priceFromError}
              required={true}
              className={classes.textInputField}
            />
          </div>
          <div className={classNames(classes.inputContainer)}>
            <TextInput
              id="priceTo"
              name="priceTo"
              label={t("price_to")}
              value={priceToValue}
              placeholder={t("to")}
              onChangeHandler={priceToChangeHandler}
              onBlurHandler={priceToInputBlurHandler}
              onClearHandler={priceToReset}
              error={priceToIsTouched && !!priceToError}
              helperText={priceToError}
              required={true}
              className={classes.textInputField}
            />
          </div>
        </div>
        {onSubmitHandler && clearValuesHandler && (
          <div className={classes.buttonsSection}>
            <FilledButton
              type="button"
              onClick={submitFilterHandler}
              content={t("apply_filters")}
              className={classes.btn}
            />
            <OutlinedButton
              type="button"
              onClick={clearValuesHandler}
              content={t("clear")}
              className={classes.btn}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default PriceFilter;
